import fs from "node:fs/promises";
import path from "node:path";

import type { SessionStoreLike } from "../session/index.js";
import type { HostTurnRunner } from "../host/types.js";
import type { RuntimeConfig, SessionRecord } from "../types.js";
import { PerPeerCommandQueue } from "./commandQueue.js";
import {
  FileTelegramAttachmentStore,
  type TelegramAttachmentStoreLike,
} from "./attachmentStore.js";
import { TelegramLongPollingSource } from "./polling.js";
import type { TelegramOffsetStoreLike } from "./offsetStore.js";
import { createConsoleTelegramLogger, type TelegramLogger } from "./logger.js";
import { chunkTelegramMessage } from "./messageChunking.js";
import type { TelegramSessionMapStoreLike } from "./sessionMapStore.js";
import { runTelegramTurn } from "./turnRunner.js";
import { summarizeText } from "./turnLogging.js";
import type { TelegramBotApiClient } from "./botApiClient.js";
import { classifyTelegramUpdate } from "./updateFilter.js";
import { TelegramDeliveryQueue } from "./deliveryQueue.js";
import { TelegramUpdateCommitQueue } from "./updateCommitQueue.js";
import type { TelegramPrivateMessage, TelegramUpdate } from "./types.js";
import { QueuedHostMessageRecorder, resolveHostStateRoot } from "../observability/hostEvents.js";
import { TelegramTurnState } from "./service/turnState.js";
import { describeIgnoredTelegramUpdate } from "./service/updateClassification.js";
import { translate } from "../i18n/index.js";
import { ControlPlaneLedger } from "../control/ledger.js";
import { collectRunningExecutionProcesses, terminateRunningExecutionProcesses } from "../execution/lifecycle.js";
import { waitAtMost } from "../remote/serviceLifecycle.js";
import { formatRemoteBlockedCommand, formatRemoteCommandHelp, parseRemoteCommand, type RemoteCommandId } from "../remote/commands.js";
import { formatRemoteRuntimeStatus } from "../remote/status.js";
import { createPersistedSession } from "../host/session.js";

export interface TelegramServiceOptions {
  cwd: string;
  config: RuntimeConfig;
  bot: TelegramBotApiClient;
  sessionStore: SessionStoreLike & {
    load(id: string): Promise<SessionRecord>;
  };
  sessionMapStore: TelegramSessionMapStoreLike;
  offsetStore: TelegramOffsetStoreLike;
  deliveryQueue: TelegramDeliveryQueue;
  attachmentStore?: TelegramAttachmentStoreLike;
  commandQueue?: PerPeerCommandQueue;
  runTurn?: HostTurnRunner;
  pollingSource?: TelegramLongPollingSource;
  logger?: TelegramLogger;
  sleep?: (ms: number) => Promise<void>;
}

export class TelegramService {
  private readonly pollingSource: TelegramLongPollingSource;
  private readonly commandQueue: PerPeerCommandQueue;
  private readonly attachmentStore: TelegramAttachmentStoreLike;
  private readonly logger: TelegramLogger;
  private readonly observability: QueuedHostMessageRecorder;
  private readonly turnState = new TelegramTurnState();
  private readonly inFlightTasks = new Set<Promise<void>>();
  private readonly pendingUpdateCommitQueue = new TelegramUpdateCommitQueue();
  private stopped = false;

  constructor(private readonly options: TelegramServiceOptions) {
    this.commandQueue = options.commandQueue ?? new PerPeerCommandQueue();
    this.pollingSource =
      options.pollingSource ??
      new TelegramLongPollingSource(options.bot, options.offsetStore, options.config.telegram);
    this.attachmentStore =
      options.attachmentStore ??
      new FileTelegramAttachmentStore(path.join(options.config.telegram.stateDir, "attachments.json"));
    this.logger = options.logger ?? createConsoleTelegramLogger();
    this.observability = new QueuedHostMessageRecorder(
      resolveHostStateRoot(options.config.telegram.stateDir, options.cwd),
      "telegram",
    );
    options.deliveryQueue.subscribe?.({
      onDeliveryFailed: (entry, error) => {
        this.observability.queue("failed", {
          direction: "outbound",
          deliveryKind: entry.kind === "file" ? "file" : "text",
          chatId: entry.chatId,
          fileName: entry.kind === "file" ? entry.fileName : undefined,
        }, error);
      },
    });
  }

  stop(): void {
    this.stopped = true;
    this.turnState.abortAllActiveTurns("Telegram service stopping.");
  }

  async run(signal?: AbortSignal): Promise<void> {
    await this.ensureStateDirectory();
    this.logger.info("service online", {
      detail: `state=${this.options.config.telegram.stateDir}`,
    });

    try {
      while (!this.stopped && !signal?.aborted) {
        try {
          await this.runPollIteration(signal);
        } catch (error) {
          if (signal?.aborted) {
            break;
          }

          this.logger.error("polling failure", {
            detail: error instanceof Error ? error.message : String(error),
          });
          await this.sleep(this.options.config.telegram.polling.retryBackoffMs);
        }
      }
    } finally {
      const sessionIds = this.turnState.listActiveSessionIds();
      await waitAtMost(this.waitForIdle(), 5_000);
      const rootDir = resolveHostStateRoot(this.options.config.telegram.stateDir, this.options.cwd);
      for (const sessionId of sessionIds) {
        const running = collectRunningExecutionProcesses(rootDir, sessionId);
        const result = terminateRunningExecutionProcesses(rootDir, running);
        if (result.failedPids.length > 0) {
          this.logger.error("shutdown process cleanup failed", {
            sessionId,
            detail: `pids=${result.failedPids.join(",")}`,
          });
        }
      }
    }
  }

  async runOnce(signal?: AbortSignal): Promise<void> {
    await this.runCommittedIteration(signal);
  }

  private async runCommittedIteration(signal?: AbortSignal): Promise<void> {
    await this.ensureStateDirectory();
    await this.options.deliveryQueue.flushDue();

    const updates = await this.pollingSource.getUpdates(signal);
    const turnTasks: Promise<void>[] = [];
    for (const update of updates) {
      const { task } = await this.processUpdate(update);
      if (task) {
        turnTasks.push(task);
      }
    }
    if (turnTasks.length > 0) {
      await Promise.all(turnTasks);
    }
    for (const update of updates) {
      await this.pollingSource.commit(update.update_id);
    }

    await this.options.deliveryQueue.flushDue();
  }

  private async runPollIteration(signal?: AbortSignal): Promise<void> {
    await this.ensureStateDirectory();
    await this.options.deliveryQueue.flushDue();

    const updates = await this.pollingSource.getUpdates(signal);

    for (const update of updates) {
      if (this.pendingUpdateCommitQueue.hasPending(update.update_id)) {
        continue;
      }

      this.pendingUpdateCommitQueue.markPending(update.update_id);
      const { task } = await this.processUpdate(update);
      this.pendingUpdateCommitQueue.queueCommit(update.update_id, task ? [task] : []);
    }

    await this.pendingUpdateCommitQueue.drain((updateId) => this.pollingSource.commit(updateId));
    await this.options.deliveryQueue.flushDue();
  }

  async waitForIdle(): Promise<void> {
    while (this.inFlightTasks.size > 0) {
      await Promise.allSettled([...this.inFlightTasks]);
    }
    await this.observability.waitForIdle();
  }

  private async processUpdate(update: TelegramUpdate): Promise<{
    task: Promise<void> | null;
  }> {
    const classified = classifyTelegramUpdate(update, {
      allowedUserIds: this.options.config.telegram.allowedUserIds,
    });
    const rootDir = resolveHostStateRoot(this.options.config.telegram.stateDir, this.options.cwd);
    const inbox = new ControlPlaneLedger(rootDir);
    let claimed: boolean;
    try {
      claimed = inbox.remoteMessages.claimInbox({
        host: "telegram",
        messageId: String(update.update_id),
        peerKey: classified.kind === "ignore" ? undefined : classified.peerKey,
      });
    } finally {
      inbox.close();
    }
    if (!claimed) return { task: null };

    if (classified.kind === "ignore") {
      this.logger.info("ignored inbound update", {
        userId: classified.userId,
        chatId: classified.chatId,
        detail: describeIgnoredTelegramUpdate(classified),
      });
      markTelegramInbox(rootDir, update.update_id, "completed");
      return { task: null };
    }

    if (classified.kind === "private_message") {
      const command = parseRemoteCommand(classified.text, "telegram");
      if (command) return this.handleRemoteCommand(command, classified, rootDir);
      if (classified.text.trim().startsWith("/")) {
        await this.enqueueReply(classified.chatId, formatRemoteBlockedCommand(this.options.config.locale));
        markTelegramInbox(rootDir, update.update_id, "completed");
        return { task: null };
      }
    }

    this.logger.info("received inbound message", {
      peerKey: classified.peerKey,
      userId: classified.userId,
      chatId: classified.chatId,
      inputKind: classified.kind === "private_file_message" ? "file" : "text",
      fileName: classified.kind === "private_file_message" ? classified.fileName : undefined,
    });
    this.observability.queue("accepted", {
      direction: "inbound",
      peerKey: classified.peerKey,
      userId: classified.userId,
      chatId: classified.chatId,
      inputKind: classified.kind === "private_file_message" ? "file" : "text",
      fileName: classified.kind === "private_file_message" ? classified.fileName : undefined,
    });

    this.turnState.incrementQueuedTurns(classified.peerKey);
    const task = this.commandQueue.enqueue(classified.peerKey, async () => {
      if (this.stopped) throw new Error("Telegram service stopped before queued update execution.");
      try {
        await runTelegramTurn({
        cwd: this.options.cwd,
        config: this.options.config,
        bot: this.options.bot,
        sessionStore: this.options.sessionStore,
        sessionMapStore: this.options.sessionMapStore,
        attachmentStore: this.attachmentStore,
        deliveryQueue: this.options.deliveryQueue,
        logger: this.logger,
        message: classified,
        runTurn: this.options.runTurn,
        enqueueReply: (chatId, text) => this.enqueueReply(chatId, text),
        markQueuedTurnStarted: (peerKey) => this.turnState.decrementQueuedTurns(peerKey),
        onActiveTurnStart: (peerKey, activeTurn) => this.turnState.setActiveTurn(peerKey, activeTurn),
        onActiveTurnEnd: (peerKey) => this.turnState.clearActiveTurn(peerKey),
        });
        markTelegramInbox(rootDir, update.update_id, "completed");
      } catch (error) {
        markTelegramInbox(rootDir, update.update_id, "failed", error);
        throw error;
      }
    });
    return {
      task: this.trackTask(task, {
        peerKey: classified.peerKey,
        userId: classified.userId,
        chatId: classified.chatId,
      }),
    };
  }

  private async handleStopCommand(message: TelegramPrivateMessage): Promise<void> {
    const activeTurn = this.turnState.getActiveTurn(message.peerKey);
    if (activeTurn && !activeTurn.controller.signal.aborted) {
      activeTurn.controller.abort();
      await this.enqueueReply(message.chatId, translate(this.options.config.locale, "remote.stopConfirmed"));
      await this.options.deliveryQueue.flushDue();
      this.logger.info("stop requested", {
        peerKey: message.peerKey,
        userId: message.userId,
        chatId: message.chatId,
        sessionId: activeTurn.sessionId,
      });
      return;
    }

    await this.enqueueReply(message.chatId, translate(this.options.config.locale, "remote.noTask"));
    await this.options.deliveryQueue.flushDue();
    this.logger.info("stop requested with no active turn", {
      peerKey: message.peerKey,
      userId: message.userId,
      chatId: message.chatId,
    });
  }

  private async handleRemoteCommand(
    command: RemoteCommandId,
    message: TelegramPrivateMessage,
    rootDir: string,
  ): Promise<{ task: Promise<void> | null }> {
    if (command === "stop") {
      await this.handleStopCommand(message);
      markTelegramInbox(rootDir, message.updateId, "completed");
      return { task: null };
    }
    if (command === "help") {
      await this.enqueueReply(message.chatId, formatRemoteCommandHelp("telegram", this.options.config.locale));
      markTelegramInbox(rootDir, message.updateId, "completed");
      return { task: null };
    }
    if (command === "status") {
      const binding = await this.options.sessionMapStore.get(message.peerKey);
      const text = binding
        ? await formatRemoteRuntimeStatus({ rootDir, config: this.options.config, sessionId: binding.sessionId })
        : translate(this.options.config.locale, "remote.noSession");
      await this.enqueueReply(message.chatId, text);
      markTelegramInbox(rootDir, message.updateId, "completed");
      return { task: null };
    }

    const activeTurn = this.turnState.getActiveTurn(message.peerKey);
    if (activeTurn && !activeTurn.controller.signal.aborted) activeTurn.controller.abort("New Telegram session requested.");
    const task = this.commandQueue.enqueue(message.peerKey, async () => {
      try {
        const session = await createPersistedSession(this.options.sessionStore, this.options.cwd);
        const now = new Date().toISOString();
        await this.options.sessionMapStore.set({
          peerKey: message.peerKey,
          userId: message.userId,
          chatId: message.chatId,
          sessionId: session.id,
          cwd: this.options.cwd,
          createdAt: now,
          updatedAt: now,
        });
        await this.enqueueReply(message.chatId, translate(this.options.config.locale, "remote.newConfirmed"));
        markTelegramInbox(rootDir, message.updateId, "completed");
      } catch (error) {
        markTelegramInbox(rootDir, message.updateId, "failed", error);
        throw error;
      }
    });
    return { task: this.trackTask(task, { peerKey: message.peerKey, userId: message.userId, chatId: message.chatId }) };
  }

  private async enqueueReply(chatId: number, text: string): Promise<void> {
    if (!text) {
      return;
    }

    for (const chunk of chunkTelegramMessage(text, this.options.config.telegram.messageChunkChars)) {
      await this.options.deliveryQueue.enqueue({
        chatId,
        text: chunk,
      });
      this.observability.queue("queued", {
        direction: "outbound",
        deliveryKind: "text",
        chatId,
      });
      this.logger.info("queued text reply", {
        chatId,
        detail: summarizeText(chunk),
      });
    }

    await this.options.deliveryQueue.flushDue();
  }

  private async ensureStateDirectory(): Promise<void> {
    await fs.mkdir(this.options.config.telegram.stateDir, { recursive: true });
  }

  private trackTask(
    task: Promise<void>,
    context: {
      peerKey: string;
      userId: number;
      chatId: number;
    },
  ): Promise<void> {
    const tracked = task
      .catch((error) => {
        this.logger.error("background task failure", {
          ...context,
          detail: error instanceof Error ? error.message : String(error),
        });
        throw error;
      })
      .finally(async () => {
        this.inFlightTasks.delete(tracked);
        try {
          await this.options.deliveryQueue.flushDue();
        } catch (error) {
          this.logger.error("delivery flush failure", {
            ...context,
            detail: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      });
    this.inFlightTasks.add(tracked);
    return tracked;
  }

  private async sleep(ms: number): Promise<void> {
    if (!Number.isFinite(ms) || ms <= 0) {
      return;
    }

    if (this.options.sleep) {
      await this.options.sleep(ms);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, ms));
  }

}

function markTelegramInbox(rootDir: string, updateId: number, status: "completed" | "failed", error?: unknown): void {
  const ledger = new ControlPlaneLedger(rootDir);
  try {
    ledger.remoteMessages.markInbox({
      host: "telegram",
      messageId: String(updateId),
      status,
      error: error instanceof Error ? error.message : error ? String(error) : undefined,
    });
  } finally {
    ledger.close();
  }
}
